import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { previewSyncAPI } from '../lib/api';

const FIELD = 'w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500 font-mono';
const LABEL = 'block text-xs text-gray-400 mb-1';

const STATE_PILL = {
  syncing: 'bg-green-900/30 text-green-300 border-green-800',
  starting: 'bg-blue-900/30 text-blue-300 border-blue-800',
  backoff: 'bg-amber-900/30 text-amber-300 border-amber-800',
  stopped: 'bg-[#1a1a1a] text-gray-400 border-[#2a2a2a]',
};
const STATE_TEXT = { syncing: 'Running', starting: 'Starting', backoff: 'Backing off', stopped: 'Stopped' };

function ago(iso) {
  if (!iso) return 'never';
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  return new Date(iso).toLocaleTimeString();
}

/**
 * Graphics Preview Sync.
 *
 * Singular UNO has no preview, so a second "shadow" app of the same composition
 * is kept in step with the live one, data only, every second. The shadow app's
 * own Control App and output (in vMix) are the preview; this page only sets the
 * pair up and shows whether it is keeping up.
 */
export default function PreviewSyncPage() {
  const [data, setData] = useState(null);
  const [source, setSource] = useState('');
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [problems, setProblems] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = () => previewSyncAPI.status().then(res => { if (!cancelled) setData(res.data); }).catch(() => {});
    load();
    const timer = setInterval(load, 1000);
    return () => { cancelled = true; clearInterval(timer); };
  }, []);

  const sync = data?.sync || { state: 'stopped', running: false };
  const config = data?.config || {};
  const saved = !!(config.source_token && config.target_token);
  const canOk = !busy && ((source.trim() || config.source_token) && (target.trim() || config.target_token));

  async function act(name, fn) {
    setBusy(name);
    setError('');
    setProblems([]);
    try {
      const res = await fn();
      if (res?.data) setData(res.data);
      return true;
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'That did not work');
      setProblems(err.response?.data?.problems || []);
      return false;
    } finally {
      setBusy('');
    }
  }

  async function ok() {
    const done = await act('ok', () => previewSyncAPI.start({ source: source.trim() || undefined, target: target.trim() || undefined }));
    if (done) { setSource(''); setTarget(''); }
  }

  const daily = sync.rateLimit?.daily;

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      <Link to="/tools" className="text-xs text-blue-500 hover:text-blue-400 transition-colors flex items-center gap-1 mb-4">
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Tools
      </Link>

      <div className="mb-5 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-white flex items-center gap-2.5">
            Graphics Preview Sync
            <span className={`px-2 py-0.5 rounded text-[11px] font-medium border ${STATE_PILL[sync.state] || STATE_PILL.stopped}`}>{STATE_TEXT[sync.state] || sync.state}</span>
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">
            Keeps a shadow Singular app's data in step with the live UNO app, so a graphic can be checked in the shadow app's Control App and output before it goes live.
          </p>
        </div>
        {sync.running
          ? <button onClick={() => act('stop', () => previewSyncAPI.stop())} disabled={!!busy} className="flex-shrink-0 px-4 py-2 bg-red-900/40 border border-red-700 hover:bg-red-900/60 disabled:opacity-40 text-red-300 text-sm rounded">{busy === 'stop' ? 'Stopping…' : 'Stop'}</button>
          : saved && <button onClick={() => act('start', () => previewSyncAPI.start({}))} disabled={!!busy} className="flex-shrink-0 px-4 py-2 bg-green-800/50 border border-green-700 hover:bg-green-800/70 disabled:opacity-40 text-green-200 text-sm rounded">{busy === 'start' ? 'Starting…' : 'Start'}</button>}
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-900/30 border border-red-700 rounded text-red-300 text-sm">
          {error}
          {problems.length > 0 && (
            <ul className="mt-2 list-disc list-inside text-xs text-red-300/90 space-y-0.5">
              {problems.slice(0, 20).map((p, i) => <li key={i}>{p}</li>)}
              {problems.length > 20 && <li>and {problems.length - 20} more</li>}
            </ul>
          )}
        </div>
      )}

      <section className="px-4 py-4 bg-[#111111] border border-[#2a2a2a] rounded space-y-3">
        <div>
          <label className={LABEL}>Source: the live app (UNO). Only ever read.</label>
          <input value={source} onChange={e => setSource(e.target.value)} placeholder={config.source_hint ? `Saved (${config.source_hint}). Paste to change.` : 'Control App API token, or a link with /controlapps/ in it'} className={FIELD} autoComplete="off" spellCheck={false} />
        </div>
        <div>
          <label className={LABEL}>Target: the shadow app, a second app of the same composition. Its data is overwritten.</label>
          <input value={target} onChange={e => setTarget(e.target.value)} placeholder={config.target_hint ? `Saved (${config.target_hint}). Paste to change.` : 'Control App API token, or a link with /controlapps/ in it'} className={FIELD} autoComplete="off" spellCheck={false} />
        </div>
        <div className="flex items-center gap-3">
          <button onClick={ok} disabled={!canOk} className="px-5 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white text-sm rounded">{busy === 'ok' ? 'Checking…' : 'OK'}</button>
          <p className="text-[11px] text-gray-500">Checks both apps, confirms they run the same composition, saves them and starts. Find a token in Singular's Dashboard: the app's i button, URLs and Token.</p>
        </div>
        <p className="text-[11px] text-gray-600 leading-relaxed">
          Data fields only: what goes In or Out on the live app is never copied, and neither are buttons (writing one presses it). Every second the live app is read and only fields that changed are written to the shadow app.
        </p>
      </section>

      <section className="mt-4 px-4 py-4 bg-[#111111] border border-[#2a2a2a] rounded">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <Fact label="Last successful sync" value={ago(sync.lastSyncAt)} />
          <Fact label="Last change copied" value={sync.lastChange ? `${ago(sync.lastChange.at)}, ${sync.lastChange.fields} field${sync.lastChange.fields === 1 ? '' : 's'}` : 'none yet'} />
          <Fact label="Writes" value={sync.running ? `${sync.writes ?? 0} (${sync.fieldsWritten ?? 0} fields)` : '-'} />
          <Fact label="Write budget today" value={daily ? `${daily.remaining.toLocaleString()} of ${daily.limit.toLocaleString()}` : '-'} />
        </div>
        {sync.running && (sync.pendingFields > 0 || sync.writesHeldForMs > 0) && (
          <p className="mt-3 text-xs text-amber-400">
            {sync.pendingFields} change{sync.pendingFields === 1 ? '' : 's'} waiting{sync.writesHeldForMs > 0 ? `; writes held ${Math.ceil(sync.writesHeldForMs / 1000)}s for Singular's rate limit` : ''}.
          </p>
        )}
        {sync.skippedFields?.length > 0 && (
          <div className="mt-3 text-xs text-amber-400">
            Singular refused {sync.skippedFields.length === 1 ? 'a field' : 'some fields'}, so {sync.skippedFields.length === 1 ? 'it is' : 'they are'} not copied:
            <ul className="mt-1 list-disc list-inside text-amber-300/80">{sync.skippedFields.map(f => <li key={f.field} className="font-mono">{f.field}</li>)}</ul>
          </div>
        )}
      </section>

      <section className="mt-4">
        <h2 className="text-xs uppercase tracking-wider text-gray-500 mb-2">Error log</h2>
        {!sync.errors?.length
          ? <p className="text-xs text-gray-600">No errors{sync.running ? ' this session' : ''}.</p>
          : (
            <div className="border border-[#2a2a2a] rounded divide-y divide-[#1f1f1f] max-h-72 overflow-y-auto">
              {sync.errors.map((e, i) => (
                <div key={i} className="px-3 py-1.5 text-xs flex gap-3">
                  <span className="text-gray-600 tabular-nums flex-shrink-0">{new Date(e.at).toLocaleTimeString()}</span>
                  <span className="text-gray-300">{e.message}</span>
                </div>
              ))}
            </div>
          )}
      </section>
    </div>
  );
}

function Fact({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className="text-white mt-0.5">{value}</div>
    </div>
  );
}
